import { useContext, useState } from 'react';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { getTwitterIdFromUrl } from '../../utils/ge-tweet-id-from-url';

import PumpFunIcon from '../assets/PumpFunIcon.png';
import BonkIcon from '../assets/baseball-bat.png';
import DexScIcon from '../assets/DexScIcon.svg';
import TwitterIcon from '../assets/TwitterIcon.svg';
import TelegramIcon from '../assets/TelegramIcon.svg';
import WebsiteIcon from '../assets/WebsiteIcon.svg';
import SearchIcon from '../assets/search.svg';
import TrashIcon from '../assets/delete.svg';
import Collapsible from '../Collapsible';
import { DeployBlockTimer } from '../DeployBlockTimer';

import { LiveDataToken, DeriveContext } from '../LiveDataToken';
import { HoverTracker } from '../HoverTracker';

import { DeriveBubble } from '../../utils/bubbles/types';
import { isBonkBubble } from '../../utils/bubbles/guards';
import { SolPriceContext } from '../SolPriceProvider';

import './Bubble.css';
import { ChannelIcon } from '../ChannelIcon';
import { getProviderLink } from '../../utils/bubbles/link';

const BUNDING_CURVE_TO_SHOW_TIMER = 15 * 60 * 1000; // 15 minutes
const TIMERS = [BUNDING_CURVE_TO_SHOW_TIMER];

type BubbleProps<T extends Api.Pump.DTO.PumpFunToken | Api.Bonk.DTO.LetsBonkToken> = {
    isCollapsed: boolean;
    isUnknownProfile?: boolean;
    copiedMint: string | null;
    onCopy: (mint: string) => void;
    onRemove: (id: number) => void;

    liveDataCtx: DeriveContext<T>;
    bubble: DeriveBubble<T>;
    getBondingCurveProgress: (token: T) => number;
    calculateMarketCap: (token: T, solPrice: number) => number;
    onNavigate: (token: T) => void;
};

export function Bubble<T extends Api.Pump.DTO.PumpFunToken | Api.Bonk.DTO.LetsBonkToken>({
    isCollapsed,
    isUnknownProfile,
    copiedMint,
    onCopy,
    onRemove,

    liveDataCtx,
    bubble,
    getBondingCurveProgress,
    calculateMarketCap,
    onNavigate,
}: BubbleProps<T>) {
    const [isBubbleOpen, setIsBubbleOpen] = useState<boolean>(true);

    const tokens = isBonkBubble(bubble) ? bubble.letsBonkTokens : bubble.pumpFunTokens;
    const bubbleType = isBonkBubble(bubble) ? 'bonk' : 'pumpfun';

    const providerIcon = isBonkBubble(bubble) ? BonkIcon : PumpFunIcon;

    const solPrice = useContext(SolPriceContext);

    return (
        <div className={`bubble ${bubbleType} ${isCollapsed ? 'expanded' : ''} ${isUnknownProfile ? '' : 'unknown-profile'}`}>
            <div className='bubble-header'>
                <p>
                    <b>{bubble.count}</b>
                </p>
                <a href={bubble.twitterUsername ? `https://x.com/${bubble.twitterUsername}` : undefined} target='_blank' rel='noopener noreferrer'>
                    <ChannelIcon icon={SearchIcon} />
                </a>
                <p>
                    <b>{bubble.twitterUsername ?? 'Unlinked'}</b>
                </p>

                <div className='header-controls__left_corner'>
                    <button className='remove-bubble-button' onClick={() => onRemove(bubble.id)}>
                        <img src={TrashIcon} alt='Remove Bubble' />
                    </button>

                    <button
                        className={`bubble-collapse-arrow ${isBubbleOpen ? 'open' : ''}`}
                        onClick={() => {
                            setIsBubbleOpen(!isBubbleOpen);
                        }}
                    ></button>
                </div>
            </div>
            <Collapsible open={isBubbleOpen}>
                {tokens.map((token) => (
                    <div className='token-info'>
                        <div className='token-name-container'>
                            <LiveDataToken token={token} ctx={liveDataCtx}>
                                {(liveDataToken) => (
                                    <div className={`token-name ${getBondingCurveProgress(liveDataToken as T) >= 100 ? 'market-cap' : ''}`}>
                                        <p>{token.name}</p>
                                        <p>{token.symbol}</p>
                                    </div>
                                )}
                            </LiveDataToken>

                            <div className='info-on-hover'>
                                <img src={token.image ?? undefined} alt={`${token.name} logo`} className='token-image-hover' />
                                <div>
                                    <p>{token.name}</p>
                                    <p>{token.symbol}</p>
                                </div>
                            </div>
                        </div>

                        <div className='token-info-logo-container'>
                            <a href={getProviderLink(bubbleType, token.mint)} target='_blank' rel='noopener noreferrer'>
                                <img className='token-info-logo' src={providerIcon} alt='Logo' />
                            </a>
                            <a href={`https://dexscreener.com/solana/${token.mint}`} target='_blank' rel='noopener noreferrer'>
                                <img className='token-info-logo' src={DexScIcon} alt='DexSc Logo' />
                            </a>
                            {isCollapsed && (
                                <button
                                    className={`${copiedMint === token.mint ? 'copiedIcon' : 'copyIcon'} token-info-logo`}
                                    onClick={() => onCopy(token.mint)}
                                />
                            )}
                        </div>

                        <DeployBlockTimer deployBlockTime={token.deployBlockTime} timers={TIMERS}>
                            {(formattedElapsedTime, [bundingCurveTimerCommpleted]) => (
                                <>
                                    {isCollapsed && (
                                        <div className='collapsed-info-container'>
                                            <div className={`bunding-curve-container ${bundingCurveTimerCommpleted ? 'hidden' : ''}`}>
                                                <LiveDataToken token={token} ctx={liveDataCtx}>
                                                    {(liveDataToken) => (
                                                        <b>
                                                            {getBondingCurveProgress(liveDataToken as T) >= 100 ? (
                                                                <p className='market-cap'>
                                                                    {formatMarketCapDisplay(calculateMarketCap(liveDataToken as T, solPrice))}
                                                                </p>
                                                            ) : (
                                                                <progress max='100' value={getBondingCurveProgress(liveDataToken as T).toFixed(0)} />
                                                            )}
                                                        </b>
                                                    )}
                                                </LiveDataToken>
                                            </div>
                                            <div className='social-logo-container'>
                                                <div className='icon-wrapper'>
                                                    <a href={token.telegram ?? undefined} target='_blank' rel='noopener noreferrer'>
                                                        <img
                                                            className={token.telegram ? 'social-logo' : 'social-logo social-logo-missing'}
                                                            src={TelegramIcon}
                                                            alt='Telegram Logo'
                                                        />
                                                    </a>
                                                </div>
                                                <HoverTracker as='div' className='icon-wrapper twitter-wrapper'>
                                                    {(hovered) => (
                                                        <>
                                                            <a href={token.twitter ?? undefined} target='_blank' rel='noopener noreferrer'>
                                                                <img
                                                                    className={token.twitter ? '-social-logo' : '-social-logo -social-logo-missing'}
                                                                    src={TwitterIcon}
                                                                    alt='Twitter Logo'
                                                                />
                                                            </a>
                                                            <div className='tweet-on-hover-container'>
                                                                {hovered && token.twitter && (
                                                                    <TwitterTweetEmbed
                                                                        tweetId={getTwitterIdFromUrl(token.twitter)}
                                                                        options={{ width: 365, theme: 'dark' }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </HoverTracker>
                                                <div className='icon-wrapper'>
                                                    <a href={token.website ?? undefined} target='_blank' rel='noopener noreferrer'>
                                                        <img
                                                            className={token.website ? 'social-logo' : 'social-logo social-logo-missing'}
                                                            src={WebsiteIcon}
                                                            alt='Website Logo'
                                                        />
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className='timestamp'>
                                        <p>{formattedElapsedTime}</p>
                                        <p>{new Date(token.deployBlockTime * 1000).toLocaleTimeString()}</p>
                                    </div>
                                </>
                            )}
                        </DeployBlockTimer>
                        {isCollapsed && (
                            <div>
                                <button
                                    className='arrow-right-icon'
                                    onClick={() => {
                                        onNavigate(token as T);
                                    }}
                                ></button>
                            </div>
                        )}
                    </div>
                ))}
            </Collapsible>
        </div>
    );
}

function formatMarketCapDisplay(value: number): string {
    return Intl.NumberFormat('en', {
        notation: 'compact',
        maximumFractionDigits: 0,
    }).format(value);
}
